import { Component, Input, OnDestroy, ViewChild } from '@angular/core';

import { defer, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as uuid from 'uuid';

import { ProminenceGroupModel } from '../../../../api/monitoring/model/prominence-group.model';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ProminenceLockerService } from '../../../../Common/Misc/services/prominence-locker.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { TextInputComponent } from '../../../../Common/Forms/components/TextInput/text-input.component';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { TakeOverPanelComponent } from '../../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';

import { ConfigService } from '../../../../Core/Config/services/config.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { AccountPreferencesErrors } from '../../enums/account-preferences-errors';
import { AccountPreferencesService } from '../../services/account-preferences.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';

@Component({
    selector: 'agpr-prominence-take-over-panel',
    templateUrl: './prominence-take-over-panel.component.html',
    styleUrls: ['./prominence-take-over-panel.component.scss'],
    standalone: false
})
export class ProminenceTakeOverPanelComponent extends AbstractDialogComponent implements OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @ViewChild('nameInput') nameInput: TextInputComponent;
    @ViewChild('panel') panel: TakeOverPanelComponent;

    hideSlideOverlay: boolean = false;

    prominenceGroups: ProminenceGroupModel[] = [];
    prominenceStatus: ProminenceRecalculationJobStatus;
    prominenceGroupLimit: number = 0;

    hasProminenceProvision: boolean;
    noProminenceContactLink: string;

    errors: AccountPreferencesErrors;

    loading: boolean = false;
    changesToSave: boolean = false;
    saveDisabled: boolean = false;
    saveDisabledTooltip: string = '';

    readonly buttonTypes = ButtonTypesEnum;
    readonly prominenceStatuses = ProminenceRecalculationJobStatus;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly splitOnPattern: RegExp = /(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g;

    // Gates the panel close on the standard unsaved-changes confirmation (mirrors the old AccountPreferencesDeactivateGuard).
    // Re-evaluated on every close attempt because the panel re-subscribes each time close() runs.
    readonly exitValidation$: Observable<boolean> = defer(() => this.accountPreferencesService.showCanLeaveDialog());

    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        public accountPreferencesService: AccountPreferencesService,
        private prominenceLockerService: ProminenceLockerService,
        private monitorConfigService: MonitorConfigService,
        private configService: ConfigService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'ProminenceTakeOverPanel');

        this.subscriptions.push(
            this.accountPreferencesService.prominenceGroups$.pipe(filter(x => !!x)).subscribe(x => this.prominenceGroups = x),
            this.prominenceLockerService.prominenceStatus$.subscribe(status => {
                this.prominenceStatus = status;
                this.setSaveDisabled();
            }),
            this.accountPreferencesService.prominenceGroupLimit$.subscribe(x => this.prominenceGroupLimit = x),
            this.monitorConfigService.getProminenceProvision().subscribe(groupsLimit => {
                this.hasProminenceProvision = groupsLimit > 0;
            }),
            this.accountPreferencesService.errors$.subscribe(errors => this.errors = errors),
            this.accountPreferencesService.changesToSave$.subscribe(changes => {
                this.changesToSave = changes && this.canSaveChanges();
                this.setSaveDisabled();
            }),
            this.accountPreferencesService.loading$.subscribe(loading => this.loading = loading)
        );
        this.setNoProminenceContactLink();
        this.prominenceLockerService.forceCheck();
    }

    canSaveChanges(): boolean {
        return !this.loading &&
            !this.errors?.promienceSubjectNameExists &&
            !this.errors?.promienceSubjectNameEmpty &&
            !this.errors?.promienceSubjectKeywordsEmpty;
    }

    setSaveDisabled(): void {
        this.saveDisabled = !this.changesToSave
            || this.prominenceStatus === this.prominenceStatuses.ONGOING
            || this.prominenceStatus === this.prominenceStatuses.PENDING
            || this.prominenceStatus === this.prominenceStatuses.FAILED;
        this.saveDisabledTooltip = this.getSaveDisabledTooltip();
    }

    getSaveDisabledTooltip(): string {
        if (this.prominenceStatus === this.prominenceStatuses.FAILED) {
            return 'AccountPreferences.prominence.disclaimer.contactEngineering';
        }
        return this.saveDisabled ? 'Account.save.noChangesToSave' : '';
    }

    addGroup() {
        this.prominenceGroups.forEach(group => group.editing = false);
        this.errors.promienceSubjectNameExists = false;
        this.accountPreferencesService.setErrors(this.errors);

        const promineceGroup = new ProminenceGroupModel();
        promineceGroup.keywords = [];
        promineceGroup.editing = true;
        promineceGroup.tempUuid = uuid.v4();

        this.prominenceGroups.push(promineceGroup);

        this.errors.promienceSubjectNameEmpty = true;
        this.errors.promienceSubjectKeywordsEmpty = true;

        this.accountPreferencesService.setErrors(this.errors);
    }

    doneEditing(group: ProminenceGroupModel) {
        if (!group.name || group.keywords.length === 0) {
            this.checkErrors();
        } else {
            if (!this.errors.promienceSubjectNameExists) {
                group.editing = false;
            }
        }
    }

    removeGroup(index: number) {
        this.coreDialogService.open(
            ConfirmDialogComponent,
            {
                inputs: {
                    heading: 'Monitoring.confirmDeleteTitle',
                    message: this.localizationService.transform('AccountPreferences.prominence.deleteMessage', { name: this.prominenceGroups[index].name }),
                    okLabel: 'AccountPreferences.prominence.contextMenu.delete',
                    cancelLabel: 'cancel',
                    width: 350
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.prominenceGroups.splice(index, 1);
                            this.checkErrors();
                        }
                    }
                }
            }
        );
    }

    checkErrors() {
        for (const group of this.prominenceGroups) {
            let id: string|number = group.id;
            if (!id) {
                id = group.tempUuid;
            }
            group.nameExists = this.accountPreferencesService.isNameDuplicate(group.name, id);
        }
        for (const group of this.prominenceGroups) {
            group.emptyName = this.isNameEmpty(group);
        }
        for (const group of this.prominenceGroups) {
            group.emptyKeywords = this.isKeywordsEmpty(group);
        }
        this.accountPreferencesService.handleErrors();
    }

    isNameEmpty(subjectGroup: ProminenceGroupModel): boolean {
        if (!subjectGroup?.name?.trim()) {
            return true;
        } else {
            return false;
        }
    }

    isKeywordsEmpty(subjectGroup: ProminenceGroupModel): boolean {
        return subjectGroup.keywords?.length === 0;
    }

    disableDone(subjectGroup: ProminenceGroupModel): boolean {
        return subjectGroup.nameExists || subjectGroup.emptyName || subjectGroup.emptyKeywords;
    }

    disableEdit(): boolean {
        return this.prominenceGroups.some(group => group.editing && (group.nameExists || group.emptyName || group.emptyKeywords))
            || this.errors.promienceSubjectNameExists || this.errors.promienceSubjectNameEmpty || this.errors.promienceSubjectKeywordsEmpty;
    }

    editGroup(index: number) {
        this.prominenceGroups.forEach(group => group.editing = false);
        this.prominenceGroups[index].editing = true;

        setTimeout(() => {
            this.nameInput.input.nativeElement.focus();
        });
    }

    /**
     * Action when the panel finishes its close animation (X, ESC, click-outside, or the footer Cancel button).
     */
    cancel(): void {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    update(): void {
        this.accountPreferencesService.saveChanges(() => this.panel.close(true, true));
    }

    private setNoProminenceContactLink() {
        if (this.configService.brandGUIDIsBurrelles()) {
            this.noProminenceContactLink = 'mailto:inquiry@burrelles.com';
        } else {
            this.noProminenceContactLink = 'https://www.agilitypr.com/contact/';
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
